/**
 * Vendored from @observertc/client-monitor-js@4.1.0
 * Source: src/scores/mosScores.ts
 * Upstream commit: 65c10c3508fb7f46c676a7622d35c25f6bf63a6c
 *
 * Original file: Copyright (c) ObserveRTC contributors
 * License: Apache-2.0 — see https://github.com/ObserveRTC/client-monitor-js/blob/master/LICENSE
 *
 * Two pure stateless MOS estimation functions based on ITU-T G.107.
 * No dependencies on the rest of @observertc/client-monitor-js.
 *
 * The exported wrappers (calculateAudioMOS / calculateVideoMOS) accept object-shaped
 * inputs using W3C webrtc-stats units (RTT in seconds, missing fields defaulted) and
 * convert to the positional helpers below.
 *
 * One deviation from upstream math — see the inline NOTE in audioMosFromPositional.
 */

export type AudioMOSInputs = {
  packetLoss?: number;
  bitrate?: number;
  roundTripTime?: number; // seconds (W3C webrtc-stats unit)
  fecEnabled?: boolean;
  dtxMode?: boolean;
  // Defaults to 0 — correct for outbound scoring (sender has no jitter buffer).
  bufferDelayInMs?: number;
};

export type VideoMOSInputs = {
  bitrate?: number;
  expectedWidth?: number;
  expectedHeight?: number;
  expectedFrameRate?: number;
  frameWidth?: number;
  frameHeight?: number;
  frameRate?: number;
  codec?: string;
  roundTripTime?: number; // seconds (W3C webrtc-stats unit)
  // Same definition as AudioMOSInputs.bufferDelayInMs. Video MOS uses
  // `delayInMs = bufferDelayInMs + RTT/2` (no 20 ms packetisation baseline).
  bufferDelayInMs?: number;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

// Math body byte-identical to upstream — do not modify.

function audioMosFromPositional(
  bitrate: number,
  packetLoss: number,
  bufferDelayInMs: number,
  roundTripTimeInMs: number,
  dtxMode: boolean,
  fec: boolean
): number {
  // Audio MOS calculation is based on E-Model algorithm
  // Assume 20ms packetization delay
  const delayInMs = 20 + bufferDelayInMs + roundTripTimeInMs / 2;
  const R0 = 100;

  // Ignore audio bitrate in dtx mode
  let equipmentImpairment: number;
  if (dtxMode) {
    equipmentImpairment = 8;
  } else if (bitrate) {
    equipmentImpairment = clamp(55 - 4.6 * Math.log(bitrate), 0, 30);
  } else {
    equipmentImpairment = 6;
  }
  const Bpl = fec ? 20 : 10;
  const Ipl = equipmentImpairment + (100 - equipmentImpairment) * (packetLoss / (packetLoss + Bpl));

  // NOTE: parentheses are required — without them JS evaluates `0.1 * delayInMs - 150` as
  // `(0.1 * delayInMs) - 150`, yielding a negative penalty for delays < 1500ms, which
  // causes higher-RTT lossy calls to score *better* than clean ones.
  const delayImpairment = delayInMs * 0.03 + (delayInMs > 150 ? 0.1 * (delayInMs - 150) : 0);
  const R = clamp(R0 - Ipl - delayImpairment, 0, 100);
  const MOS = 1 + 0.035 * R + (R * (R - 60) * (100 - R) * 7) / 1000000;

  return clamp(Math.round(MOS * 100) / 100, 1, 5);
}

function videoMosFromPositional(
  bitrate: number,
  expectedWidth: number,
  expectedHeight: number,
  bufferDelayInMs: number,
  roundTripTimeInMs: number,
  codec: string,
  frameRate: number,
  expectedFrameRate: number
): number {
  const pixels = expectedWidth * expectedHeight;
  const codecFactor = codec === 'vp9' ? 1.2 : 1.0;
  const delayInMs = bufferDelayInMs + roundTripTimeInMs / 2;
  if (frameRate < 1) {
    return 1.0;
  }
  const bPPPF = (codecFactor * bitrate) / pixels / frameRate;
  const base = clamp(0.56 * Math.log(bPPPF) + 5.36, 1, 5);
  const MOS = base - 1.9 * Math.log(expectedFrameRate / frameRate) - delayInMs * 0.002;

  return clamp(Math.round(MOS * 100) / 100, 1, 5);
}

const normalizeCodec = (codec: string | undefined): string => {
  if (!codec) {
    return '';
  }
  const sub = codec.includes('/') ? (codec.split('/')[1] ?? codec) : codec;
  return sub.toLowerCase();
};

export function calculateAudioMOS(inputs: AudioMOSInputs): number {
  return audioMosFromPositional(
    inputs.bitrate ?? 0,
    inputs.packetLoss ?? 0,
    inputs.bufferDelayInMs ?? 0,
    (inputs.roundTripTime ?? 0) * 1000, // seconds → ms
    inputs.dtxMode ?? false,
    inputs.fecEnabled ?? false
  );
}

export function calculateVideoMOS(inputs: VideoMOSInputs): number {
  return videoMosFromPositional(
    inputs.bitrate ?? 0,
    inputs.expectedWidth ?? inputs.frameWidth ?? 0,
    inputs.expectedHeight ?? inputs.frameHeight ?? 0,
    inputs.bufferDelayInMs ?? 0,
    (inputs.roundTripTime ?? 0) * 1000, // seconds → ms
    normalizeCodec(inputs.codec),
    inputs.frameRate ?? 0,
    inputs.expectedFrameRate ?? inputs.frameRate ?? 30
  );
}
